import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAlert, useAlerts, type Alert } from '../hooks/useAlerts'
import { Card, Pill, SectionLabel, DecisionSummary } from '../components/ui'
import {
  timeAgo, localTimestamp, dropPct, getSev, getTitle, getTypeBadge, sevColor,
} from '../lib/alertFormat'

// ── Static styles (theme-token driven; both themes follow automatically) ──────
const s: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--text-1)' },
  topbar: { padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, background: 'var(--surface)' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', background: 'none', border: 'none' },
  sep: { width: 1, height: 16, background: 'var(--border-2)' },
  title: { fontSize: 17, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.02em' },
  actions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 },
  btn: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1.5px solid var(--border-2)', background: 'var(--surface-3)', color: 'var(--text-1)' },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', boxShadow: 'var(--cta-shadow)' },
  reviewedPill: { display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, fontFamily: 'var(--sans)', border: '1.5px solid rgba(11,184,184,0.30)', background: 'var(--accent-soft)', color: 'var(--accent)' },
  scroll: { flex: 1, overflowY: 'auto', padding: '28px 32px', minHeight: 0 },
}

const cardBase: React.CSSProperties = { padding: '22px 28px', marginBottom: 20 }

function Icon({ path, size = 12, w = '2' }: { path: React.ReactNode; size?: number; w?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w}>{path}</svg>
}
const chevronR = <polyline points="9 18 15 12 9 6" />
const chevronL = <polyline points="15 18 9 12 15 6" />
const check = <polyline points="20 6 9 17 4 12" />

// Business-type → human label.
function businessLabel(t?: string | null): string | null {
  if (t === 'storefront') return 'Storefront'
  if (t === 'delivery') return 'Delivery'
  return null
}

function ageDays(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000))
}

function reviewAge(alert: Alert): string {
  const days = ageDays(alert.created_at)
  return days >= 7 ? `Overdue · ${days} days (7-day review policy)` : timeAgo(alert.created_at)
}

// The operator's live relationship to this rival (honest — from the tracking slot).
function statusMeta(alert: Alert): { label: string; color: string } {
  if (alert.slot_type === 'track') return { label: '● Actively tracked', color: 'var(--accent)' }
  return { label: 'Market observation', color: 'var(--text-2)' }
}

// Honest one-line summary of the change — never references a price we don't hold.
function heroSub(alert: Alert): React.ReactNode {
  const product = alert.entity_name || 'a tracked SKU'
  if ((alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase') && alert.old_value && alert.new_value) {
    const pct = dropPct(alert)
    const dir = alert.alert_type === 'price_increase' ? 'raised' : 'cut'
    return (
      <>
        {alert.competitor_name} {dir} <strong style={{ color: 'var(--text-1)' }}>{product}</strong> from ${alert.old_value} to ${alert.new_value}
        {pct != null && <> — a <strong style={{ color: sevColor(getSev(alert)) }}>{Math.abs(pct)}% {alert.alert_type === 'price_increase' ? 'increase' : 'drop'}</strong></>}. This is a competitive pricing move observed on the rival's public menu.
      </>
    )
  }
  if (alert.alert_type === 'new_promo') return <>{alert.competitor_name} launched a new promotion detected on their public menu.</>
  if (alert.alert_type === 'new_sku') return <>{alert.competitor_name} added new products to their public menu.</>
  return <>New activity detected for {alert.competitor_name}.</>
}

function decisionCopy(alert: Alert): { answer: string; change: string; impact: string; action: string } {
  if (alert.alert_type === 'price_drop' && alert.old_value && alert.new_value) {
    const pct = dropPct(alert)
    return {
      answer: `Compare your price before responding to ${alert.competitor_name}'s ${pct}% cut`,
      change: `${alert.competitor_name} moved this listed price from $${alert.old_value} to $${alert.new_value} ${timeAgo(alert.created_at)}.`,
      impact: `Your current price, unit economics, and sales exposure are not included in this alert. The observed ${pct}% cut is a pricing-risk signal, not proof of lost volume.`,
      action: 'Review price change evidence →',
    }
  }
  if (alert.alert_type === 'new_promo') return {
    answer: `Verify the promotion from ${alert.competitor_name} before matching it`,
    change: `A new promotion was detected on the rival's public menu ${timeAgo(alert.created_at)} and remains unreviewed.`,
    impact: `Offer terms, SKU overlap, and commercial impact are not included in this alert, so a matching response is not justified yet.`,
    action: 'Check shared products →',
  }
  if (alert.alert_type === 'new_sku') return {
    answer: `Compare ${alert.competitor_name}'s additions with your catalog`,
    change: `New products were detected on the rival's public menu ${timeAgo(alert.created_at)}.`,
    impact: `The alert does not quantify assortment overlap or sales opportunity; use the catalog comparison before changing your assortment.`,
    action: 'Compare catalog →',
  }
  return {
    answer: `Review ${alert.competitor_name}'s latest activity before responding`,
    change: `New rival activity was detected ${timeAgo(alert.created_at)}.`,
    impact: `Commercial impact is not quantified in the current alert data.`,
    action: 'Review supporting data →',
  }
}

export default function AlertDetail() {
  const navigate = useNavigate()
  const { alertId } = useParams<{ alertId: string }>()
  const { alert, loading, error, notFound, markReviewed, refetch } = useAlert(alertId)
  const [toast, setToast] = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2600)
  }

  const handleMarkReviewed = async () => {
    try {
      await markReviewed()
      showToast('Alert marked as reviewed')
    } catch {
      showToast("Couldn't mark reviewed — we'll retry")
    }
  }

  // ── Non-content states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={s.root}>
        <Topbar navigate={navigate} />
        <div style={s.scroll}>
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>
            Pulling this alert's latest intelligence…
          </div>
        </div>
      </div>
    )
  }

  if (notFound || (!alert && !error)) {
    return (
      <div style={s.root}>
        <Topbar navigate={navigate} />
        <div style={s.scroll}>
          <EmptyState
            title="This alert isn't available"
            body="It may already be resolved, or the rival is no longer in your active tracking. Head back to the Alert Feed to see what's live."
            onBack={() => navigate('/alerts')}
          />
        </div>
      </div>
    )
  }

  if (error || !alert) {
    return (
      <div style={s.root}>
        <Topbar navigate={navigate} />
        <div style={s.scroll}>
          <EmptyState
            title="Couldn't load this alert"
            body="We hit a snag reaching the intelligence feed. It usually clears on its own — try again in a moment."
            onBack={() => refetch()}
            backLabel="Retry"
          />
        </div>
      </div>
    )
  }

  // ── Content ─────────────────────────────────────────────────────────────
  const sev = getSev(alert)
  const accent = sevColor(sev)
  const status = statusMeta(alert)
  const oldV = alert.old_value ? parseFloat(alert.old_value) : NaN
  const newV = alert.new_value ? parseFloat(alert.new_value) : NaN
  const hasPrice = isFinite(oldV) && isFinite(newV)
  const isDrop = alert.alert_type !== 'price_increase'
  const isPriceMove = alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase'
  const delta = hasPrice ? newV - oldV : 0
  const pct = dropPct(alert)
  const decision = decisionCopy(alert)
  const bizLabel = businessLabel(alert.business_type)

  const productLine = [alert.entity_name, alert.entity_category, alert.entity_package_size]
    .filter(Boolean).join(' · ')

  const meta: { label: string; val: React.ReactNode }[] = [
    { label: 'Rival', val: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--rose)', display: 'inline-block' }} />
        {alert.competitor_name}
      </span>
    ) },
    { label: 'Status', val: <span style={{ color: status.color }}>{status.label}</span> },
    { label: 'Type', val: getTypeBadge(alert) },
    { label: 'Detected', val: <span style={{ fontFamily: 'var(--mono)' }}>{localTimestamp(alert.created_at)}</span> },
    { label: 'Review age', val: <span style={{ fontFamily: 'var(--mono)', color: ageDays(alert.created_at) >= 7 ? 'var(--danger)' : 'var(--text-2)' }}>{reviewAge(alert)}</span> },
    { label: 'Confidence', val: alert.confidence ? alert.confidence[0].toUpperCase() + alert.confidence.slice(1) : '—' },
  ]
  if (bizLabel) meta.push({ label: 'Operator', val: bizLabel })
  if (alert.reviewed && alert.reviewed_at) meta.push({ label: 'Reviewed', val: <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{localTimestamp(alert.reviewed_at)}</span> })

  return (
    <div style={s.root}>
      <Topbar
        navigate={navigate}
        reviewed={alert.reviewed}
        onMarkReviewed={handleMarkReviewed}
      />

      <div style={s.scroll}>
        {/* ── ALERT HERO ─────────────────────────────────────────────── */}
        <Card style={{ ...cardBase, borderLeft: `4px solid ${accent}` }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {alert.location_name && <><span>{alert.location_name}</span><Icon path={chevronR} size={8} /></>}
            <span>{alert.competitor_name}</span>
            <Icon path={chevronR} size={8} />
            <Pill variant="chip" tone={sev === 'medium' ? 'accent' : sev === 'info' ? 'neutral' : 'danger'} style={{ fontSize: 9, letterSpacing: '0.10em' }}>{getTypeBadge(alert)}</Pill>
          </div>
          <div style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 6, lineHeight: 1.2, color: 'var(--text-1)' }}>
            {getTitle(alert)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18, lineHeight: 1.55, maxWidth: 720 }}>{heroSub(alert)}</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {meta.map(({ label, val }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{val}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── PRICE MOVEMENT (price alerts only) ─────────────────────── */}
        {hasPrice && (
          <Card style={cardBase}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Price Movement</div>
            {productLine && <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 16 }}>{productLine}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', padding: '16px 28px', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)', marginBottom: 6 }}>Previous Price</div>
                <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 32, fontWeight: 700, color: 'var(--text-3)', textDecoration: 'line-through', lineHeight: 1 }}>${oldV.toFixed(oldV % 1 ? 2 : 0)}</div>
              </div>
              <div style={{ fontSize: 28, color: 'var(--text-3)' }}>→</div>
              <div style={{ textAlign: 'center', padding: '16px 28px', borderRadius: 'var(--r-sm)', background: 'var(--accent-soft)', border: `1px solid ${isDrop ? 'rgba(11,184,184,0.3)' : 'var(--danger-soft)'}` }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDrop ? 'var(--accent)' : 'var(--danger)', marginBottom: 6 }}>New Price</div>
                <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 32, fontWeight: 700, color: isDrop ? 'var(--accent)' : 'var(--danger)', lineHeight: 1 }}>${newV.toFixed(newV % 1 ? 2 : 0)}</div>
              </div>
              <div style={{ width: 1, height: 60, background: 'var(--border-2)', margin: '0 4px' }} />
              <div style={{ textAlign: 'center' }}>
                <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: isDrop ? 'var(--accent)' : 'var(--danger)' }}>
                  {delta < 0 ? '−' : '+'}${Math.abs(delta).toFixed(Math.abs(delta) % 1 ? 2 : 0)}
                </div>
                {pct != null && <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>{delta < 0 ? '−' : '+'}{Math.abs(pct)}%</div>}
              </div>
            </div>
          </Card>
        )}

        {/* ── DECISION BRIEF ─────────────────────────────────────────── */}
        <DecisionSummary
          className="alert-detail-summary"
          question="What should I do about this alert?"
          priority={sev === 'critical' ? 'critical' : sev === 'high' ? 'high' : 'watch'}
          answer={decision.answer}
          change={decision.change}
          impact={decision.impact}
          provenance={{
            updatedAt: localTimestamp(alert.created_at),
            source: 'rival public menu observation',
            confidence: alert.confidence === 'high' ? 'high' : alert.confidence === 'medium' ? 'medium' : 'unavailable',
          }}
          action={(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
              <button
                data-testid="alert-detail-primary-action"
                onClick={() => navigate(isPriceMove ? `/prices/change/${alert.id}` : '/prices')}
                style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid var(--accent)', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}
              >
                {decision.action}
              </button>
              {!alert.reviewed && (
                <button onClick={handleMarkReviewed} style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                  Mark as reviewed
                </button>
              )}
            </div>
          )}
        >
          <div className="alert-detail-summary__completion">
            <strong>Completion means:</strong> Review the supporting data and record the alert as seen. “Reviewed” does not record a match, hold, or assortment decision.
          </div>
        </DecisionSummary>

        {/* ── RELATED ALERTS FROM THIS RIVAL ─────────────────────────── */}
        <RelatedAlerts
          competitorId={alert.competitor_id}
          locationId={alert.location_id}
          competitorName={alert.competitor_name}
          currentId={alert.id}
          onOpen={(id) => navigate(`/alerts/${id}`)}
        />
      </div>

      {/* ── TOAST ────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--surface-solid, var(--surface))', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '11px 18px', fontSize: 12, fontWeight: 600, boxShadow: 'var(--card-shadow-lg)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          {toast}
        </div>
      )}
    </div>
  )
}

// ── Topbar ───────────────────────────────────────────────────────────────────
function Topbar({ navigate, reviewed, onMarkReviewed }: {
  navigate: ReturnType<typeof useNavigate>
  reviewed?: boolean
  onMarkReviewed?: () => void
}) {
  return (
    <div style={s.topbar}>
      <button style={s.backBtn} onClick={() => navigate('/alerts')}>
        <Icon path={chevronL} size={12} w="2.5" />
        Alert Feed
      </button>
      <div style={s.sep} />
      <div style={s.title}>Alert Detail</div>
      <div style={s.actions}>
        {onMarkReviewed && (
          reviewed ? (
            <span style={s.reviewedPill}><Icon path={check} size={12} />Reviewed</span>
          ) : (
            <button style={s.btn} onClick={onMarkReviewed}><Icon path={check} size={12} />Mark reviewed</button>
          )
        )}
      </div>
    </div>
  )
}

// ── Related alerts ────────────────────────────────────────────────────────────
function RelatedAlerts({ competitorId, locationId, competitorName, currentId, onOpen }: {
  competitorId?: string | null
  locationId?: string | null
  competitorName: string
  currentId: string
  onOpen: (id: string) => void
}) {
  const { alerts, loading } = useAlerts({
    competitorId: competitorId ?? undefined,
    locationId: locationId ?? undefined,
    reviewed: 'all',
    limit: 8,
  })
  const related = alerts.filter((a) => a.id !== currentId).slice(0, 6)

  return (
    <div style={{ marginTop: 4 }}>
      <SectionLabel style={{ marginBottom: 10 }}>Related alerts from {competitorName}</SectionLabel>
      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 2px' }}>Checking this rival's recent activity…</div>
      ) : related.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
          No other recent alerts from {competitorName}. This is the only event on record for now.
        </div>
      ) : (
        related.map((a) => {
          const sev = getSev(a)
          return (
            <div key={a.id} onClick={() => onOpen(a.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', border: '1px solid var(--border)', marginBottom: 5, cursor: 'pointer' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: sevColor(sev), flexShrink: 0 }} />
              <div style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getTitle(a)}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>{timeAgo(a.created_at)}</div>
            </div>
          )
        })
      )}
    </div>
  )
}

// ── Empty / error state ───────────────────────────────────────────────────────
function EmptyState({ title, body, onBack, backLabel = 'Back to Alert Feed' }: {
  title: string
  body: string
  onBack: () => void
  backLabel?: string
}) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px', maxWidth: 420, margin: '0 auto' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--text-3)' }}>
        <Icon path={<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>} size={20} />
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.01em' }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6, marginBottom: 20 }}>{body}</div>
      <button onClick={onBack} style={{ padding: '9px 18px', borderRadius: 20, border: 'none', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}>{backLabel}</button>
    </div>
  )
}
